import {
  Component,
  Inject,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, ActivityTypeEnum } from '@sport-activities/sdk';
import { Subscription } from 'rxjs';

import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { Type } from '~/component/form/input/enums/type.enum';
import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { ModalVariantEnum } from '~/component/modal/enums/modal-variant.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { TranslationKeySubPathEnum } from '~/core/enums/translation-key-sub-path.enum';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { PriceValidator } from '~/core/validators/price/price.validator';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedPageContainerTipConfigInterface } from '~/organizer/shared/components/shared/page-container/interfaces/tip.config.interface';
import { FormPlansEditionModeEnum } from '~/organizer/shared/components/shared/plans/plans.component';
import { buildOfferIdentifier } from '~/organizer/shared/helpers/offer/build-offer-identifier/build-offer-identifier.helper';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { appConfig } from '~/app.config';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';

@Component({
  selector: 'app-organizer-activity-creation-plans',
  templateUrl: './plans.component.html',
})
export class OrganizerActivityCreationPagePlansComponent extends AbstractOrganizerActivityCreationFormPageDirective {
  @ViewChildren('nameInput')
  public inputs: QueryList<unknown>;

  @ViewChild(ModalComponent) public modal: ModalComponent;

  private readonly subscriptions = new Subscription();

  protected readonly pageTitleKey = `seo.organizer.${this.translateKey(
    'title'
  )}`;

  public readonly modalVariantEnum = ModalVariantEnum;
  public readonly tipsVariant = TipsVariantEnum;
  public readonly formPlansEditionMode = FormPlansEditionModeEnum;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly toggleNameEnum = ToggleNameEnum;
  public readonly translationKeySubPath = TranslationKeySubPathEnum;
  public readonly vtmnButtonVariantEnum = VtmnButtonVariantEnum;

  public inputType = Type;
  public isMobileDevice: boolean;
  public tipsConfig: OrganizerSharedPageContainerTipConfigInterface[];

  constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: UntypedFormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService,
    private readonly matchMediaService: MatchMediaService,
    private readonly locatableService: OrganizerSharedLocatableService,
    private readonly toggleService: ToggleService,
    private readonly translateService: TranslateService,
    private readonly organizerSharedOrganizationService: OrganizerSharedOrganizationService,
    private readonly priceValidator: PriceValidator
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  public get isFreeActivityOnly(): boolean {
    return this.toggleService.isEnabled(ToggleNameEnum.ActivityFreeOnly);
  }

  public get translationKeyPrefix(): string {
    if (this.isBirthday) {
      return this.translationKeySubPath.BIRTHDAY;
    } else if (this.isProductTest) {
      return this.translationKeySubPath.PRODUCT_TEST;
    } else {
      return '';
    }
  }

  public get currency(): string {
    return this.toggleService
      .getVariant(ToggleNameEnum.Currency, 'EUR')
      .toString()
      ?.split(',')?.[0];
  }

  public get isBirthday(): boolean {
    return (
      this.activityService.activity.type === ('BIRTHDAY' as ActivityTypeEnum)
    );
  }

  public get isProductTest(): boolean {
    return (
      this.activityService.activity?.type ===
      ('PRODUCT_TEST' as ActivityTypeEnum)
    );
  }

  private get isSkipOfferNameSlugifyEnabled(): boolean {
    return this.toggleService.isEnabled(
      this.toggleNameEnum.SkipOfferNameSlugify
    );
  }

  public get sectionDescription(): string {
    return (
      (!this.isBirthday || !this.isProductTest) &&
      this.translateKey('section.description')
    );
  }

  protected buildActivityData(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    const data = super.buildActivityData(formGroupValue);
    let baseActivity: Partial<ActivityInterface> = {
      indicativeOffers: formGroupValue.plans.map((plan) => ({
        ...plan,
        currency: this.currency,
        identifier: buildOfferIdentifier(
          plan.name,
          this.isSkipOfferNameSlugifyEnabled
        ),
        price: +plan.price,
      })),
      typicalAgeRange: data.typicalAgeRange,
      meta: data.meta,
    };

    if (this.isBirthday || this.isProductTest) {
      baseActivity = {
        ...baseActivity,
        indicativeMaximumAttendeeCapacity:
          data.indicativeMaximumAttendeeCapacity,
      };
    }
    return baseActivity;
  }

  protected buildDataBeforeSubmit(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    if (this.isBirthday || this.isProductTest) {
      return {
        indicativeMaximumAttendeeCapacity: 1, // only one slot on birthday
        sports: [999999], // multisport as default sport
        typicalAgeRange: {
          min: this.isBirthday
            ? 1
            : +this.toggleService.getVariant(ToggleNameEnum.MajorityAge, '18'),
          max: this.isBirthday
            ? +this.toggleService.getVariant(ToggleNameEnum.MajorityAge, '18') -
              1
            : appConfig.formValidationRules.activityMaxAgeInputMax,
        },
        indicativeOffers: formGroupValue.plans.map((plan) => {
          return {
            identifier: buildOfferIdentifier(
              plan.name,
              this.isSkipOfferNameSlugifyEnabled
            ),
            name: [this.locatableService.buildCurrentLocatableBody(plan.name)],
            description: [
              this.locatableService.buildCurrentLocatableBody(plan.description),
            ],
            currency: this.currency,
            price: +plan.price,
            meta: plan.meta,
          };
        }),
        meta: {
          ...this.activityService.activity.meta,
          plansMaximumCapacity: formGroupValue.plansMaximumCapacity,
        },
      };
    }

    return {
      typicalAgeRange: this.activityService.activity.typicalAgeRange,
      indicativeOffers: formGroupValue.plans.map((plan) => {
        return {
          identifier: buildOfferIdentifier(
            plan.name,
            this.isSkipOfferNameSlugifyEnabled
          ),
          name: [this.locatableService.buildCurrentLocatableBody(plan.name)],
          currency: this.currency,
          price: +plan.price,
        };
      }),
    };
  }

  protected buildValidationConfig(): void {
    let plans = this.activityService.activity?.indicativeOffers;

    if (
      !plans?.length ||
      this.toggleService.isEnabled(ToggleNameEnum.ActivityFreeOnly)
    ) {
      const defaultName = this.isProductTest
        ? this.translateService.instant(
            'component.form.plans.product_test.name'
          )
        : this.translateService.instant('component.form.plans.default.name');
      plans = [
        {
          identifier: buildOfferIdentifier(
            defaultName,
            this.isSkipOfferNameSlugifyEnabled
          ),
          name: defaultName,
          description: '',
          currency: this.currency,
          price:
            this.organizationService.organization?.paymentProviderStatus !==
            'LIVE'
              ? 0
              : undefined,
        },
      ];
    }

    this.validationConfig = {
      plans: [
        plans,
        [
          this.priceValidator.validatePaymentProvider(
            this.organizerSharedOrganizationService.organization
          ),
        ],
      ],
    };

    if (this.isBirthday || this.isProductTest) {
      this.validationConfig = {
        ...this.validationConfig,
        plansMaximumCapacity: [
          this.activityService.activity?.meta?.plansMaximumCapacity || 1,
          [Validators.required, Validators.min(1)],
        ],
      };
    }
  }

  protected onInitHook(): void {
    this.tipsConfig = [
      {
        descriptionKey: this.isFreeActivityOnly
          ? this.translateKey('tips.1.description.price.free')
          : this.translateKey('tips.1.description.price.all'),
        headerKey: this.translateKey('tips.1.header'),
        variant: this.isBirthday
          ? TipsVariantEnum.WARNING
          : TipsVariantEnum.INFO,
      },
    ];

    if (this.isBirthday) {
      this.tipsConfig.push({
        descriptionKey: this.translateKey('tips.2.description'),
        headerKey: this.translateKey('tips.2.header'),
        variant: TipsVariantEnum.INFO,
      });
    }

    if (
      this.toggleService.isEnabled(ToggleNameEnum.AllowExternalPayment) &&
      !this.toggleService.isEnabled(ToggleNameEnum.ActivityFreeOnly)
    ) {
      this.tipsConfig.unshift({
        descriptionKey: this.translateKey('tips.payment_on_site.description'),
        headerKey: this.translateKey('tips.payment_on_site.header'),
        variant: TipsVariantEnum.INFO,
      });
    }

    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );
  }

  public submit(formGroupValue: FormGroupValueInterface): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    super.submit(formGroupValue);
  }

  public translateKey(key: string, interpolateParams?: object): string {
    let baseKey = 'activity_creation.plans.';

    if (this.isBirthday) {
      baseKey = baseKey.concat(TranslationKeySubPathEnum.BIRTHDAY);
    }

    if (this.isProductTest) {
      baseKey = baseKey.concat(TranslationKeySubPathEnum.PRODUCT_TEST);
    }

    return this.translateService.instant(`${baseKey}${key}`, interpolateParams);
  }
}
